import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { linkTokenService, inviteService, getErrorMessage } from '../../services/apiService';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import Icon from '../../components/AppIcon';
import { useToast } from '../../components/ui/Toast';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import InputDialog from '../../components/ui/InputDialog';
import LinkTokenFilters from './components/LinkTokenFilters';
import StageTabs from './components/StageTabs';
import LinkTokenTable from './components/LinkTokenTable';
import ExtendDialog from './components/ExtendDialog';
import RegenerateResultModal from './components/RegenerateResultModal';
import JourneyDrawer from './components/JourneyDrawer';
import { useLinkTokenQueryParams } from './hooks/useLinkTokenQueryParams';
import InviteVendorModal from '../vendor-approval-workflow/components/InviteVendorModal';
import { STAGE_LABELS, copyToClipboard } from './utils/linkTokenFormat';

// Temporary maintenance switch. Flip to false to restore the full screen.
const UNDER_MAINTENANCE = false;

const InviteManagement = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { filters, setFilters, clearFilters, hasActiveFilters } = useLinkTokenQueryParams();

  // Invites still waiting for their approver have no link yet, so they are not in the register
  // below; a count here points to where they are (Approvals › Invite Approval).
  const [pendingInvites, setPendingInvites] = useState(0);
  const fetchPendingInvites = useCallback(async () => {
    try {
      const res = await inviteService.getApprovals({ status: 'PENDING_APPROVAL', limit: 1 });
      setPendingInvites(res?.meta?.total ?? 0);
    } catch {
      setPendingInvites(0);
    }
  }, []);
  useEffect(() => { fetchPendingInvites(); }, [fetchPendingInvites]);

  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });

  // Stage tab counts (honor every filter except stage).
  const [stageCounts, setStageCounts] = useState({ counts: {}, total: 0 });

  // The active stage tab: a single selected stage, else null ("All").
  const activeStage = filters.stage.length === 1 ? filters.stage[0] : null;

  // Action dialog state
  const [regenTarget, setRegenTarget] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenResult, setRegenResult] = useState(null);
  const [remindingId, setRemindingId] = useState(null);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendLoading, setExtendLoading] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [journey, setJourney] = useState(null); // { vendorId, tokenId }
  const [showInvite, setShowInvite] = useState(false);

  const fetchTokens = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: filters.page,
        limit: filters.limit,
        sort: filters.sort,
        order: filters.order,
        ...(filters.q && { q: filters.q }),
        ...(filters.stage.length > 0 && { stage: filters.stage }),
        ...(filters.status.length > 0 && { status: filters.status }),
        ...(filters.expiring_within_days && {
          expiring_within_days: filters.expiring_within_days,
        }),
        ...(filters.expired_unactioned && { expired_unactioned: true }),
      };
      const response = await linkTokenService.getLinkTokens(params);
      if (response.success) {
        setTokens(response.data || []);
        if (response.meta) {
          setPagination({
            page: response.meta.page,
            limit: response.meta.limit,
            total: response.meta.total,
            totalPages: response.meta.totalPages,
          });
        }
      }
    } catch (err) {
      console.error('Error fetching link tokens:', err);
      setError(getErrorMessage(err));
      toast.error('Failed to load links');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.page,
    filters.limit,
    filters.q,
    filters.stage.join(','),
    filters.status.join(','),
    filters.expiring_within_days,
    filters.expired_unactioned,
    filters.sort,
    filters.order,
  ]);

  useEffect(() => {
    if (UNDER_MAINTENANCE) return;
    fetchTokens();
  }, [fetchTokens]);

  // Stage tab counts — honor every filter EXCEPT stage (so each tab shows how
  // many links fall in that category given the current status/search filters).
  const fetchCounts = useCallback(async () => {
    try {
      const params = {
        ...(filters.q && { q: filters.q }),
        ...(filters.status.length > 0 && { status: filters.status }),
        ...(filters.expiring_within_days && {
          expiring_within_days: filters.expiring_within_days,
        }),
        ...(filters.expired_unactioned && { expired_unactioned: true }),
      };
      const response = await linkTokenService.getStageCounts(params);
      if (response.success) {
        setStageCounts({ counts: response.data.counts || {}, total: response.data.total || 0 });
      }
    } catch (err) {
      // Counts are non-critical chrome; don't surface an error toast for them.
      console.error('Error fetching stage counts:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.q,
    filters.status.join(','),
    filters.expiring_within_days,
    filters.expired_unactioned,
  ]);

  useEffect(() => {
    if (UNDER_MAINTENANCE) return;
    fetchCounts();
  }, [fetchCounts]);

  // Refresh both the list and the tab counts after a mutation.
  const refresh = useCallback(() => {
    fetchTokens();
    fetchCounts();
  }, [fetchTokens, fetchCounts]);

  const handlePageChange = (newPage) => setFilters({ page: newPage });

  // Tab click: select a single stage, or clear it for "All".
  const handleStageTab = (stage) => setFilters({ stage: stage ? [stage] : [] });

  // ---- Copy ----
  const handleCopy = async (row) => {
    try {
      const res = await linkTokenService.getLink(row.id);
      if (res?.data?.live && res.data.link) {
        await copyToClipboard(res.data.link);
        toast.success('Link copied to clipboard');
      } else {
        toast.error('No live link is available for this row');
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to copy link');
    }
  };

  // ---- Regenerate ----
  const handleRegenerateConfirm = async () => {
    if (!regenTarget) return;
    setRegenLoading(true);
    try {
      const res = await linkTokenService.regenerate(regenTarget.id, {});
      setRegenTarget(null);
      if (res.success) {
        setRegenResult(res.data);
        toast.success('Link regenerated');
        refresh();
      }
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      if (code === 'REGENERATION_LIMIT_EXCEEDED') {
        toast.error(
          'Regeneration limit reached. A supervisor (ADMIN) must perform this regeneration.'
        );
      } else if (code === 'NOT_IMPLEMENTED') {
        toast.error('Regeneration is not available for this stage yet.');
      } else {
        toast.error(getErrorMessage(err) || 'Failed to regenerate link');
      }
      setRegenTarget(null);
    } finally {
      setRegenLoading(false);
    }
  };

  // ---- Remind ----
  // Nudges the vendor about a link that is still open. The backend enforces a
  // 24h per-recipient limit shared with the automated reminders, so a refusal
  // here is expected behaviour, not an error to panic about.
  const handleRemind = async (row) => {
    setRemindingId(row.id);
    try {
      const res = await linkTokenService.remind(row.id);
      toast.success(res.message || `Reminder sent to ${row.recipient_email}`);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to send reminder');
    } finally {
      setRemindingId(null);
    }
  };

  // ---- Extend ----
  const handleExtendSubmit = async ({ days, reason }) => {
    if (!extendTarget) return;
    setExtendLoading(true);
    try {
      await linkTokenService.extend(extendTarget.id, { days, reason });
      toast.success('Expiry extended');
      setExtendTarget(null);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to extend expiry');
    } finally {
      setExtendLoading(false);
    }
  };

  // ---- Revoke ----
  const handleRevokeSubmit = async (reason) => {
    if (!revokeTarget) return;
    if (!reason || reason.trim().length < 3) {
      toast.error('A reason of at least 3 characters is required');
      return;
    }
    setRevokeLoading(true);
    try {
      await linkTokenService.revoke(revokeTarget.id, { reason: reason.trim() });
      toast.success('Link revoked');
      setRevokeTarget(null);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to revoke link');
    } finally {
      setRevokeLoading(false);
    }
  };

  const regenVendor = regenTarget?.vendor_company_name || regenTarget?.recipient_email || 'this vendor';
  const regenStage = regenTarget ? STAGE_LABELS[regenTarget.stage] || regenTarget.stage : '';

  if (UNDER_MAINTENANCE) {
    return (
      <div className="space-y-5">
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/dashboard-overview' },
              { label: 'Invite Management', path: '/invite-management' },
            ]}
          />
        </div>

        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="py-16 px-6 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <Icon name="Wrench" size={32} className="text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Under Maintenance</h1>
            <p className="mx-auto mt-2 max-w-md text-slate-600">
              Invite Management is temporarily unavailable while we perform scheduled
              maintenance. Please check back shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="mb-6">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/dashboard-overview' },
            { label: 'Invite Management', path: '/invite-management' },
          ]}
        />
      </div>

      {/* Header */}
      {/* NOTE: label kept as "Invite Management" per instructions (rename needs
          sign-off). This screen now covers ALL vendor onboarding links, not just
          invites — suggested rename to "Vendor Links" is in the phase report. */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Invite Management</h1>
            <p className="text-slate-600 mt-1">
              All vendor onboarding links across every stage — track, extend, revoke and regenerate
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => setShowInvite(true)}
              className="flex items-center"
              title="Invite Vendor"
            >
              <Icon name="UserPlus" size={18} className="mr-2" />
              Invite Vendor
            </Button>
          </div>
        </div>
      </div>

      {pendingInvites > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50" data-testid="pending-invites-banner">
          <Icon name="Clock" size={18} className="text-amber-700 flex-shrink-0" />
          <p className="text-sm text-amber-900 flex-1">
            <strong>{pendingInvites}</strong> invite{pendingInvites === 1 ? ' is' : 's are'} awaiting approval. The registration link is
            sent, and appears here, once the approver approves.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/vendor-approval-workflow?tab=invites')}>
            Open Invite Approval
          </Button>
        </div>
      )}

      {/* Filters */}
      <LinkTokenFilters
        filters={filters}
        onChange={setFilters}
        onClear={clearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Table (stage tabs as the card header) */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <StageTabs
          activeStage={activeStage}
          counts={stageCounts.counts}
          total={stageCounts.total}
          onChange={handleStageTab}
        />
        <CardContent className="p-0">
          {error ? (
            <div className="p-6 text-center">
              <Icon name="AlertCircle" size={48} className="mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Error Loading Links</h3>
              <p className="text-slate-500 mb-4">{error}</p>
              <Button onClick={fetchTokens} variant="outline">
                Retry
              </Button>
            </div>
          ) : (
            <LinkTokenTable
              tokens={tokens}
              loading={loading}
              pagination={pagination}
              onPageChange={handlePageChange}
              onRegenerate={setRegenTarget}
              onRemind={handleRemind}
              remindingId={remindingId}
              onExtend={setExtendTarget}
              onRevoke={setRevokeTarget}
              onCopy={handleCopy}
              onViewTimeline={(row) => setJourney({ vendorId: row.vendor_id, tokenId: row.id })}
            />
          )}
        </CardContent>
      </Card>

      {/* Regenerate confirmation */}
      <ConfirmDialog
        isOpen={Boolean(regenTarget)}
        onClose={() => setRegenTarget(null)}
        onConfirm={handleRegenerateConfirm}
        title="Regenerate Link"
        message={`Generate a fresh ${regenStage} link for ${regenVendor}? A new token is issued and the previous one is superseded.`}
        variant="warning"
        confirmText="Regenerate"
        loading={regenLoading}
      />

      {/* Regenerate result (link + copy + email status) */}
      {regenResult && (
        <RegenerateResultModal result={regenResult} onClose={() => setRegenResult(null)} />
      )}

      {/* Extend expiry */}
      <ExtendDialog
        isOpen={Boolean(extendTarget)}
        onClose={() => setExtendTarget(null)}
        onSubmit={handleExtendSubmit}
        loading={extendLoading}
        vendorName={extendTarget?.vendor_company_name || extendTarget?.recipient_email || 'this vendor'}
        stageLabel={extendTarget ? STAGE_LABELS[extendTarget.stage] || extendTarget.stage : ''}
      />

      {/* Revoke (single required reason — reuses shared InputDialog) */}
      <InputDialog
        isOpen={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        onSubmit={handleRevokeSubmit}
        title="Revoke Link"
        message={`Revoke the ${
          revokeTarget ? STAGE_LABELS[revokeTarget.stage] || revokeTarget.stage : ''
        } link for ${
          revokeTarget?.vendor_company_name || revokeTarget?.recipient_email || 'this vendor'
        }? This invalidates the current link.`}
        inputLabel="Reason"
        inputPlaceholder="Why is this being revoked?"
        submitText="Revoke"
        required
        loading={revokeLoading}
      />

      {/* Journey drawer */}
      {journey && (
        <JourneyDrawer
          vendorId={journey.vendorId}
          initialTokenId={journey.tokenId}
          onClose={() => setJourney(null)}
        />
      )}

      {/* Invite a new vendor (sends a registration link) */}
      {showInvite && (
        <InviteVendorModal
          onClose={() => setShowInvite(false)}
          onSuccess={() => {
            setShowInvite(false);
            refresh();
          }}
        />
      )}

    </div>
  );
};

export default InviteManagement;
